import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Carregando from "../componentes/Carregando";
import { Cabine } from "../componentes/FerramentaUi";
import { TileDeFato } from "../componentes/CalendarioUi";
import { LegendaDaEscala } from "../componentes/MapaUi";
import { rolarAte, useRolarAoEscolher } from "../lib/rolarPara";
import {
  AlternadorDeTipo,
  Bloco,
  Checklist,
  ChipDeStatus,
  ESCALA_DE_STATUS,
  LinkExterno,
  ListaDeTribunais,
  MapaDeTribunais,
  SeletorDeStatus,
  escalaDeRegioes,
} from "../componentes/ForoFacilUi";
import {
  BASE,
  DICAS,
  definirStatus,
  documentosDo,
  jurisdicaoDoTrf,
  lerCatalogo,
  lerMeuProgresso,
  marcadosDo,
  marcarDocumento,
  resumo,
  statusDe,
  tribunaisDoTipo,
  type Catalogo,
  type Progresso,
  type StatusDoCadastro,
  type TipoDeTribunal,
  type Tribunal,
} from "../lib/forofacil";

// O ForoFácil: o guia de credenciamento como perito nos 27 Tribunais de Justiça
// e nos 6 Tribunais Regionais Federais — com o registro do cadastro do aluno.
//
// ---------------------------------------------------------------------------
// De onde isto veio
// ---------------------------------------------------------------------------
// Portado do app da Lovable (`github.com/Contabilidade-Facilitada/forofacil`),
// ferramenta do curso de Perícia, a pedido de Victor em 02/09/2026. O que mudou
// está em `docs/runbooks/forofacil.md`; em resumo:
//
//   · os 33 tribunais e os 403 documentos saíram de uma constante de 888 linhas
//     (revisada 4 vezes, todas em 18/03/2026) para `foro_tribunais` e
//     `foro_documentos`, com a aba Admin › ForoFácil e trilha de conferência;
//   · o progresso do aluno saiu do `localStorage` do navegador dele para o
//     banco, com RLS de dono — e o MAPA passou a mostrá-lo (a origem o ignorava
//     e pintava por região);
//   · Pessoa Jurídica ganhou checklist (na origem era só leitura);
//   · o gerador de currículo próprio NÃO veio: a Perícia ganhou o card do
//     Gerador de Currículos da Escola (000170);
//   · os seis TRFs entram com a nota "lista genérica, por conferir" à vista.
//
// ---------------------------------------------------------------------------
// A tela é UMA, e o tribunal abre AO LADO do mapa (03/09/2026)
// ---------------------------------------------------------------------------
// A primeira versão levava a uma tela dedicada por tribunal: o mapa sumia, e
// para comparar dois tribunais o aluno voltava, olhava o mapa, clicava de novo.
// Victor pediu o contrário — *"ao clicar em um estado, abrisse ao lado do mapa
// as informações do tribunal"* —, e ele está certo pelo uso: escolher tribunal é
// uma sequência, não uma visita.
//
// Então o mapa é uma COLUNA que fica (grudada no alto enquanto a ficha rola), e
// a coluna da direita é a ficha do tribunal aberto ou, quando não há nenhum, a
// lista dos 27 ou dos 6. A rota NÃO mudou: `/SP` continua sendo endereço, para o
// aluno mandar o link do tribunal dele a um colega — o que mudou é que ela pinta
// uma coluna em vez de trocar a tela.
//
// ---------------------------------------------------------------------------
// Os segmentos, e o que é RESERVADO
// ---------------------------------------------------------------------------
//   /ferramenta/foro-facil              o mapa (e `?modo=trf` para os federais)
//   /ferramenta/foro-facil/SP           o mapa com o TJ de São Paulo ao lado
//   /ferramenta/foro-facil/TRF4         idem, para um TRF
//
// O único segmento é a SIGLA — um dado, não uma palavra reservada. Se um dia a
// ferramenta ganhar conteúdo pelo motor da 000073, nenhuma seção pode se chamar
// "SP" nem "TRF4".

export default function ForoFacil() {
  const { "*": resto = "" } = useParams();
  const sigla = resto.split("/").filter(Boolean)[0]?.toUpperCase() ?? null;

  const [catalogo, setCatalogo] = useState<Catalogo | null>(null);
  const [progresso, setProgresso] = useState<Progresso | null>(null);
  const [fase, setFase] = useState<"carregando" | "pronto" | "erro">("carregando");

  useEffect(() => {
    let vivo = true;
    Promise.all([lerCatalogo(), lerMeuProgresso()]).then(([c, p]) => {
      if (!vivo) return;
      setCatalogo(c);
      // Sem progresso a tela AINDA abre: o catálogo é o que informa, e o
      // progresso vazio é um estado legítimo (o aluno novo).
      setProgresso(p ?? { status: new Map(), marcados: new Set() });
      setFase(c ? "pronto" : "erro");
    });
    return () => {
      vivo = false;
    };
  }, []);

  if (fase === "carregando") return <Carregando />;
  if (fase === "erro" || !catalogo) {
    return (
      <Vazio titulo="Não conseguimos carregar os tribunais">
        Tente de novo em instantes. Se continuar, avise o suporte.
      </Vazio>
    );
  }

  const tribunal = sigla ? (catalogo.tribunais.find((t) => t.sigla === sigla) ?? null) : null;

  if (sigla && !tribunal) {
    return (
      <Vazio titulo={`Não existe tribunal com a sigla "${sigla}"`}>
        Escolha um dos 33 na lista abaixo.
        <div className="mt-6 text-left">
          <ListaDeTribunais
            catalogo={catalogo}
            tribunais={[...tribunaisDoTipo(catalogo, "tj"), ...tribunaisDoTipo(catalogo, "trf")]}
            progresso={progresso}
            base={BASE}
          />
        </div>
      </Vazio>
    );
  }

  return (
    <TelaDoForo
      catalogo={catalogo}
      progresso={progresso}
      setProgresso={setProgresso}
      tribunal={tribunal}
    />
  );
}

// ---------------------------------------------------------------------------
// A tela: o mapa à esquerda, o tribunal (ou a lista) à direita
// ---------------------------------------------------------------------------

function TelaDoForo({
  catalogo,
  progresso,
  setProgresso,
  tribunal,
}: {
  catalogo: Catalogo;
  progresso: Progresso | null;
  setProgresso: (p: Progresso) => void;
  tribunal: Tribunal | null;
}) {
  const navegar = useNavigate();
  const [params, setParams] = useSearchParams();

  // O modo vem da QUERY, mas o tribunal ABERTO manda: entrar direto em
  // `/TRF4` tem de mostrar o mapa dos federais, senão o mapa e a ficha ao lado
  // falam de coisas diferentes.
  const modo: TipoDeTribunal = tribunal ? tribunal.tipo : params.get("modo") === "trf" ? "trf" : "tj";

  const fatos = useMemo(() => resumo(catalogo, progresso), [catalogo, progresso]);
  const tjs = useMemo(() => tribunaisDoTipo(catalogo, "tj"), [catalogo]);
  const trfs = useMemo(() => tribunaisDoTipo(catalogo, "trf"), [catalogo]);
  // A contagem de "por conferir" saiu junto com o aviso que ela alimentava
  // (03/09/2026, pedido de Victor). Ela vive na aba Admin › ForoFácil, que é o
  // lugar de fila interna — aqui era um número que o aluno não podia usar.

  const painel = useRef<HTMLDivElement>(null);
  const dicas = useRef<HTMLElement>(null);
  // Na largura de celular as duas colunas viram uma, e a ficha nasce abaixo do
  // mapa — fora do campo de visão. É a cicatriz de 28/08: a escolha acontece
  // longe do resultado dela, e a pessoa acha que o clique não pegou.
  useRolarAoEscolher(painel, tribunal?.sigla ?? null);

  const trocarModo = (m: TipoDeTribunal) => {
    const novos = new URLSearchParams(params);
    if (m === "tj") novos.delete("modo");
    else novos.set("modo", m);
    // Trocar de modo com uma ficha aberta volta ao endereço do mapa: o tribunal
    // que estava ao lado não é do tipo que passou a ser mostrado.
    if (tribunal) navegar(`${BASE}?${novos}`, { replace: true });
    else setParams(novos, { replace: true });
  };

  return (
    // Mais largura que as outras ferramentas, e por um motivo: aqui são duas
    // colunas de conteúdo lado a lado, não uma coluna de leitura.
    <div className="mx-auto w-full max-w-[86rem] px-4 py-8 sm:px-6">
      <Cabine
        rotulo="FERRAMENTA"
        titulo="ForoFácil"
        descricao={
          <>
            O seu cadastro como perito nos 27 Tribunais de Justiça e nos 6 Tribunais Regionais
            Federais: onde se cadastrar, que documentos cada um pede, e o quanto você já andou.
          </>
        }
        acao={
          // O botão que Victor pediu no topo. É um `<button>` e não uma âncora
          // porque a rolagem é a da casa: desconta o Topo fixo por `--cf-topo`,
          // respeita `prefers-reduced-motion` e não rouba o foco — `href="#…"`
          // saltaria por baixo do Topo e mexeria no foco.
          <button
            type="button"
            data-ir-para-dicas
            onClick={() => rolarAte(dicas.current)}
            className="pill-cta inline-flex items-center gap-2"
          >
            Dicas de aceite
            <span aria-hidden="true">↓</span>
          </button>
        }
        fatos={
          <>
            <TileDeFato rotulo="Tribunais" valor={String(fatos.tribunais)} apoio="27 estaduais e 6 federais" />
            {fatos.concluidos > 0 && (
              <TileDeFato
                rotulo="Cadastros concluídos"
                valor={String(fatos.concluidos)}
                apoio={fatos.concluidos === 1 ? "tribunal" : "tribunais"}
              />
            )}
            {fatos.emAndamento > 0 && (
              <TileDeFato
                rotulo="Em andamento"
                valor={String(fatos.emAndamento)}
                apoio={fatos.emAndamento === 1 ? "tribunal" : "tribunais"}
              />
            )}
            {fatos.marcados > 0 && (
              <TileDeFato
                rotulo="Documentos reunidos"
                valor={String(fatos.marcados)}
                apoio="marcados nas listas"
              />
            )}
          </>
        }
      />

      <div className="mt-6 grid items-start gap-6 lg:grid-cols-[minmax(0,23rem)_minmax(0,1fr)] xl:grid-cols-[minmax(0,29rem)_minmax(0,1fr)]">
        {/*
          O mapa FICA. Grudado no alto na largura larga, ele continua à vista
          enquanto a ficha do tribunal rola — que é o ponto de abrir ao lado em
          vez de navegar: escolher tribunal é uma sequência.
        */}
        <div className="lg:sticky lg:top-[calc(var(--cf-topo)+1rem)]">
          <div className="painel-navy rounded-cf-lg p-5 sm:p-6">
            <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-navy-mute">
              Ver no mapa
            </h2>
            <div className="mt-2.5">
              <AlternadorDeTipo
                modo={modo}
                onTrocar={trocarModo}
                contagem={{ tj: tjs.length, trf: trfs.length }}
              />
            </div>
            <MapaDeTribunais
              catalogo={catalogo}
              progresso={progresso}
              modo={modo}
              selecionado={tribunal?.sigla ?? null}
              onEscolher={(s) => navegar(`${BASE}/${s}`)}
            />
            <div className="mt-1">
              <LegendaDaEscala
                escala={modo === "tj" ? ESCALA_DE_STATUS : escalaDeRegioes(trfs)}
                titulo={modo === "tj" ? "Seu cadastro" : "Região e cadastro"}
              />
            </div>
            <p className="mt-4 text-xs leading-relaxed text-navy-mute">
              {modo === "tj"
                ? "Clique no estado para abrir o Tribunal de Justiça dele aqui ao lado."
                : "Cada cor é um TRF, e a sua região acende quando você marca o cadastro. Clique em qualquer estado para abrir o TRF dele."}
            </p>
          </div>
        </div>

        <div ref={painel}>
          {tribunal ? (
            <FichaDoTribunal
              catalogo={catalogo}
              tribunal={tribunal}
              progresso={progresso}
              setProgresso={setProgresso}
              voltar={tribunal.tipo === "trf" ? `${BASE}?modo=trf` : BASE}
            />
          ) : (
            <section
              role="group"
              aria-labelledby="lista-de-tribunais"
              className="surface p-5 sm:p-6"
            >
              <h2 id="lista-de-tribunais" className="font-display text-lg font-bold text-titulo">
                {modo === "tj" ? "Os 27 Tribunais de Justiça" : "Os 6 Tribunais Regionais Federais"}
              </h2>
              <p className="mt-1 text-sm text-cinza">
                {modo === "tj"
                  ? "Um por estado. Ao lado de cada um, quantos documentos você já reuniu."
                  : "Cada TRF cobre um grupo de estados; a ficha de cada um diz quais."}
              </p>
              <div className="mt-4">
                <ListaDeTribunais
                  catalogo={catalogo}
                  tribunais={modo === "tj" ? tjs : trfs}
                  progresso={progresso}
                  base={BASE}
                />
              </div>
              {/*
                O aviso de "N tribunais por conferir pela equipe" SAIU em
                03/09/2026, por pedido de Victor: *"não é pra mencionar isso pro
                aluno"*.

                E ele está certo pelo desenho da casa: fila interna da equipe não
                é assunto de quem usa a ferramenta. O aluno não pode fazer nada
                com essa informação, e ela só ensina a desconfiar do catálogo
                inteiro — inclusive das 20 fichas que já foram conferidas.
                Contagem de pendência da equipe é tela de ADMIN (Admin ›
                ForoFácil), onde ela vira trabalho.

                O que fica ao aluno é a NOTA de cada tribunal, que é informação
                útil sobre AQUELE tribunal ("a lista de documentos é genérica") —
                sem dizer de quem é a tarefa de resolver.
              */}
            </section>
          )}
        </div>
      </div>

      <section ref={dicas} aria-labelledby="dicas-de-aceite" className="mt-8">
        <h2 id="dicas-de-aceite" className="font-display text-lg font-bold text-titulo">
          Dicas de aceite
        </h2>
        <p className="mt-1 text-sm text-cinza">
          Orientações práticas para o cadastro ser aceito — valem para qualquer tribunal.
        </p>
        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          {DICAS.map((d) => (
            <Bloco key={d.titulo} titulo={d.titulo}>
              <ol className="space-y-2.5">
                {d.itens.map((item, i) => (
                  <li key={item} className="flex gap-2.5 text-sm leading-relaxed text-tinta">
                    <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-chip text-[10px] font-bold text-chip-tinta">
                      {i + 1}
                    </span>
                    {item}
                  </li>
                ))}
              </ol>
            </Bloco>
          ))}
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// A ficha do tribunal — a coluna da direita
// ---------------------------------------------------------------------------

function FichaDoTribunal({
  catalogo,
  tribunal,
  progresso,
  setProgresso,
  voltar,
}: {
  catalogo: Catalogo;
  tribunal: Tribunal;
  progresso: Progresso | null;
  setProgresso: (p: Progresso) => void;
  voltar: string;
}) {
  const status = statusDe(progresso, tribunal.sigla);
  const pf = documentosDo(catalogo, tribunal.sigla, "pf");
  const pj = documentosDo(catalogo, tribunal.sigla, "pj");
  const contagem = marcadosDo(catalogo, progresso, tribunal.sigla);
  const trfDoTj = tribunal.trf ? catalogo.tribunais.find((t) => t.sigla === tribunal.trf) : null;
  const jurisdicao = tribunal.tipo === "trf" ? jurisdicaoDoTrf(catalogo, tribunal.sigla) : [];

  const [gravandoStatus, setGravandoStatus] = useState(false);
  const [erroStatus, setErroStatus] = useState<string | null>(null);
  const [errosDeTique, setErrosDeTique] = useState<Map<string, string>>(new Map());

  const atual = (): Progresso => progresso ?? { status: new Map(), marcados: new Set() };

  // Otimista, com volta: a tela mostra o tique na hora e desfaz se o banco
  // recusar — a mensagem fica NA LINHA, não no lugar da tela.
  async function marcar(id: string, marcado: boolean) {
    const antes = atual();
    const depois: Progresso = { status: antes.status, marcados: new Set(antes.marcados) };
    if (marcado) depois.marcados.add(id);
    else depois.marcados.delete(id);
    setProgresso(depois);
    setErrosDeTique((m) => {
      const n = new Map(m);
      n.delete(id);
      return n;
    });
    const erro = await marcarDocumento(id, marcado);
    if (erro) {
      setProgresso(antes);
      setErrosDeTique((m) => new Map(m).set(id, erro));
    }
  }

  async function trocarStatus(novo: StatusDoCadastro) {
    const antes = atual();
    const depois: Progresso = { status: new Map(antes.status), marcados: antes.marcados };
    if (novo === "nao_iniciado") depois.status.delete(tribunal.sigla);
    else depois.status.set(tribunal.sigla, novo);
    setProgresso(depois);
    setGravandoStatus(true);
    setErroStatus(null);
    const erro = await definirStatus(tribunal.sigla, novo);
    setGravandoStatus(false);
    if (erro) {
      setProgresso(antes);
      setErroStatus(erro);
    }
  }

  const ehTj = tribunal.tipo === "tj";

  return (
    // `<h2>` e não `<h1>`: o `<h1>` da página é o da Cabine ("ForoFácil"), e a
    // ficha agora convive com ela na MESMA tela. Duas `<h1>` foi o que o
    // instrumento de captura acusou na primeira rodada deste redesenho — na tela
    // dedicada de antes a ficha era a página inteira e o `<h1>` era dela.
    <div className="space-y-6" aria-labelledby="ficha-do-tribunal">
      <header className="surface p-5 sm:p-6">
        <Link
          to={voltar}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-link hover:underline"
        >
          <span aria-hidden="true">‹</span>{" "}
          {ehTj ? "Todos os Tribunais de Justiça" : "Todos os Tribunais Regionais Federais"}
        </Link>
        <p className="mt-3 font-display text-xs font-bold uppercase tracking-widest text-laranja-texto">
          {ehTj ? "Tribunal de Justiça" : "Tribunal Regional Federal"} · {tribunal.sistema}
        </p>
        <h2
          id="ficha-do-tribunal"
          className="mt-1.5 font-display text-2xl font-bold text-titulo sm:text-3xl"
        >
          {tribunal.nome_completo}
        </h2>
        <p className="mt-1 text-sm text-cinza">
          {ehTj ? (
            <>
              Região <span className="font-semibold text-tinta">{tribunal.regiao}</span>
              {trfDoTj && (
                <>
                  {" · "}Justiça Federal pelo{" "}
                  <Link to={`${BASE}/${trfDoTj.sigla}`} className="font-semibold text-link underline">
                    {trfDoTj.nome}
                  </Link>
                </>
              )}
            </>
          ) : (
            <>
              Sede em <span className="font-semibold text-tinta">{tribunal.sede}</span>
              {jurisdicao.length > 0 && (
                <>
                  {" · "}Jurisdição:{" "}
                  <span className="font-semibold text-tinta">
                    {jurisdicao.map((t) => t.sigla).join(", ")}
                  </span>
                </>
              )}
            </>
          )}
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <a
            href={tribunal.link_cadastro}
            target="_blank"
            rel="noreferrer"
            data-acessar-cadastro
            className="pill-cta inline-flex items-center gap-2"
          >
            Acessar o cadastro no tribunal
            <span aria-hidden="true">↗</span>
            <span className="sr-only">(abre em outra aba)</span>
          </a>
          <ChipDeStatus status={status} />
          {/*
            O selo "links por conferir pela equipe" SAIU em 03/09/2026, por
            pedido de Victor. Ele ficava ao lado do botão que leva ao portal do
            tribunal, e o efeito era o oposto do pretendido: o aluno lia "não
            confie neste link" antes de clicar, sem ter o que fazer a respeito.

            Quem precisa dessa lista é a equipe, na aba Admin › ForoFácil, onde
            ela é trabalho a fazer. A nota específica do tribunal continua na
            ficha, abaixo — ela informa sobre o TRIBUNAL, não sobre a nossa fila.
          */}
        </div>
      </header>

      <Bloco
        titulo="Meu cadastro neste tribunal"
        descricao={
          contagem.total > 0
            ? `${contagem.marcados} de ${contagem.total} documentos reunidos.`
            : "Este tribunal ainda não tem lista de documentos cadastrada."
        }
      >
        <SeletorDeStatus valor={status} onChange={trocarStatus} ocupado={gravandoStatus} />
        {erroStatus && (
          <p role="alert" className="mt-2 text-sm text-erro-tinta">
            O status não gravou ({erroStatus}). O resto da página continua aqui — tente de novo.
          </p>
        )}
      </Bloco>

      <Bloco
        titulo="Documentos — pessoa física"
        descricao={
          pf.length > 0 ? "Marque o que você já reuniu. O tique fica gravado na sua conta." : undefined
        }
      >
        {pf.length > 0 ? (
          <Checklist
            documentos={pf}
            marcados={progresso?.marcados ?? new Set()}
            onMarcar={marcar}
            erros={errosDeTique}
          />
        ) : (
          <p className="text-sm text-cinza">
            Este tribunal não tem lista de documentos para pessoa física.
          </p>
        )}
      </Bloco>

      {/*
        A lista PJ APARECE mesmo vazia, dizendo isso com letra. Na origem 26 dos
        33 tribunais não têm a lista, e a aba PJ mostrava um itálico cinza.
        Quando a ausência carrega informação, ela precisa de texto próprio.
      */}
      <Bloco
        titulo="Documentos — pessoa jurídica"
        descricao={pj.length > 0 ? "Para quem se credencia como empresa." : undefined}
      >
        {pj.length > 0 ? (
          <Checklist
            documentos={pj}
            marcados={progresso?.marcados ?? new Set()}
            onMarcar={marcar}
            erros={errosDeTique}
          />
        ) : (
          <p className="text-sm leading-relaxed text-cinza">
            A lista para pessoa jurídica não veio do ForoFácil antigo para este tribunal. Se o
            cadastro como empresa for o seu caso, confira as exigências no site oficial.
          </p>
        )}
      </Bloco>

      {tribunal.alerta && (
        <div className="rounded-cf border border-alerta-tinta/30 bg-alerta-fundo p-4 text-sm leading-relaxed text-tinta">
          <span className="font-display text-xs font-bold uppercase tracking-widest text-alerta-tinta">
            Atenção
          </span>
          <p className="mt-1">{tribunal.alerta}</p>
        </div>
      )}

      <Bloco titulo="Informações">
        <dl className="grid gap-4 text-sm sm:grid-cols-2">
          <Info rotulo="Sistema">{tribunal.sistema}</Info>
          {ehTj ? (
            <>
              <Info rotulo="Região">{tribunal.regiao}</Info>
              <Info rotulo="Justiça Federal">
                {trfDoTj ? `${trfDoTj.nome} — sede em ${trfDoTj.sede}` : "—"}
              </Info>
            </>
          ) : (
            <>
              <Info rotulo="Sede">{tribunal.sede}</Info>
              <Info rotulo="Jurisdição">
                {jurisdicao.length > 0 ? jurisdicao.map((t) => t.nome_completo).join(", ") : "—"}
              </Info>
            </>
          )}
          <Info rotulo="Contato">{tribunal.contato ?? "Não informado"}</Info>
          <Info rotulo="Site oficial">
            <LinkExterno href={tribunal.site}>{tribunal.site.replace(/^https:\/\//, "")}</LinkExterno>
          </Info>
          <Info rotulo="Edital de credenciamento">
            {tribunal.edital ? (
              <LinkExterno href={tribunal.edital}>Ver edital</LinkExterno>
            ) : (
              <span className="text-cinza">Não localizado — confira no site oficial.</span>
            )}
          </Info>
          <Info rotulo="Tutorial">
            {tribunal.tutorial ? (
              <LinkExterno href={tribunal.tutorial}>Ver tutorial</LinkExterno>
            ) : (
              <span className="text-cinza">O tribunal não publica tutorial.</span>
            )}
          </Info>
        </dl>
        {/*
          A nota de origem é a PISTA da conferência, e ela aparece ao aluno de
          propósito: dizer "os TRFs têm lista genérica" é melhor do que ele
          reunir seis documentos e descobrir no balcão que faltavam dez. Some
          quando a equipe confere — a conferência é a resposta a ela.

          O que SAIU em 03/09/2026, por pedido de Victor, foi a frase "Por
          conferir pela equipe" que abria o parágrafo. A NOTA fica, porque ela
          fala do tribunal e o aluno faz algo com ela; a menção à nossa fila
          interna sai, porque ele não faz nada com essa parte. O rótulo passou a
          dizer o que a informação É — "atenção sobre este tribunal" — em vez de
          dizer de quem é a tarefa.
        */}
        {!tribunal.conferido_em && tribunal.nota_de_origem && (
          <p
            data-nota-de-origem
            className="mt-4 rounded-cf bg-superficie-2 p-3 text-xs leading-relaxed text-cinza"
          >
            <span className="font-semibold text-tinta">Atenção sobre este tribunal.</span>{" "}
            {tribunal.nota_de_origem}
          </p>
        )}
      </Bloco>
    </div>
  );
}

function Info({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-[0.09em] text-cinza">{rotulo}</dt>
      <dd className="mt-0.5 break-words text-tinta">{children}</dd>
    </div>
  );
}

function Vazio({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <main className="mx-auto max-w-3xl px-4 py-14 text-center sm:px-6">
      <h1 className="font-display text-2xl font-bold text-titulo">{titulo}</h1>
      <div className="mt-2 text-cinza">{children}</div>
    </main>
  );
}
